import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../utils/helpers/hydration';

const COMPONENT_NAME = 'breadcrumbs-type-1';

/**
 * Breadcrumb navigation component
 *
 * @prop {string} [separator] - Icon name for separator between items (default: 'arrows--chevron-right')
 * @prop {string} [ariaLabel] - Accessible label for the breadcrumb navigation (default: 'Breadcrumb')
 * @prop {string} [theme] - Theme variant for the breadcrumb component (default: 'light')
 * @prop {boolean} [withSchemaOrgEntities] - Add schema.org microdata markup (default: true)
 */

@Component({
  tag: 'breadcrumbs-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class BreadcrumbsType1 {

  /** Array of breadcrumb items
   * @type {array}
   * @items.type {object}
   * @items.property {string} label - Text to display for the breadcrumb item
   * @items.property {string} href - Link URL (optional, if not provided item will be non-clickable)
   * @items.required label
   * @minItems 1
   *
   * */

  @Prop({ mutable: true }) items!: any[] | string;

  private parsedItems: any[] = [];

  componentWillLoad() {
    // Parse items from JSON string (SSR) or use array directly
    const parsed = parseJsonProp(this.items, []);
    this.parsedItems = Array.isArray(parsed) ? parsed : [];
  }

  /** Icon name for separator between items */
  @Prop() separator: string = 'arrows--chevron-right';

  /** Accessible label for the breadcrumb navigation */
  @Prop() ariaLabel: string = 'Breadcrumb';

  /** Theme variant for the breadcrumb component */
  @Prop() theme: 'light' | 'dark' = 'light';

  /** Add schema.org microdata markup */
  @Prop() withSchemaOrgEntities: boolean = true;

  render() {
    const items = this.parsedItems || [];

    if (!items || items.length === 0) {
      return null;
    }

    return (
      <me-section yGap="sm" yGapLg="sm">
        <nav
          class={`${COMPONENT_NAME} ${COMPONENT_NAME}--${this.theme}`}
          aria-label={this.ariaLabel}
          role="navigation"
          {...(this.withSchemaOrgEntities
            ? {
                itemscope: true,
                itemtype: 'https://schema.org/BreadcrumbList',
              }
            : {})}
        >
          <ol class={`${COMPONENT_NAME}__list`}>
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              const isCurrent = isLast;

              return (
                <li
                  class={`${COMPONENT_NAME}__item ${isCurrent ? `${COMPONENT_NAME}__item--current` : ''}`}
                  {...(this.withSchemaOrgEntities
                    ? {
                        itemscope: true,
                        itemtype: 'https://schema.org/ListItem',
                        itemprop: 'itemListElement',
                      }
                    : {})}
                >
                  {item.href && !isCurrent ? (
                    <a class={`${COMPONENT_NAME}__link`} href={item.href} aria-label={item.label} {...(this.withSchemaOrgEntities ? { itemprop: 'item' } : {})}>
                      <span {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>{item.label}</span>
                    </a>
                  ) : (
                    <span class={`${COMPONENT_NAME}__text`} aria-current={isCurrent ? 'page' : undefined} {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>
                      {item.label}
                    </span>
                  )}

                  {this.withSchemaOrgEntities && isCurrent && item.href && <meta itemprop="item" content={item.href} />}

                  {!isLast && (
                    <span class={`${COMPONENT_NAME}__separator`} aria-hidden="true">
                      <me-icon name={this.separator} inline />
                    </span>
                  )}

                  {this.withSchemaOrgEntities && <meta itemprop="position" content={String(index + 1)} />}
                </li>
              );
            })}
          </ol>
        </nav>
      </me-section>
    );
  }
}
